import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  MenuItem,
  Switch,
  TextField,
  Tooltip,
  Typography,
  useTheme
} from "@material-ui/core";
import ArrowDownwardIcon from "@material-ui/icons/ArrowDownward";
import ArrowUpwardIcon from "@material-ui/icons/ArrowUpward";
import CheckIcon from "@material-ui/icons/Check";
import CloseIcon from "@material-ui/icons/Close";
import FilterListIcon from "@material-ui/icons/FilterList";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import RemoveIcon from "@material-ui/icons/Remove";
import UndoIcon from "@material-ui/icons/Undo";
import WarningIcon from "@material-ui/icons/Warning";
import { Button, ContainerPage, TitlePage } from "components";
import { usePopup } from "hooks/usePopup";
import { useTableParams } from "hooks/useTableParams";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import pricingService from "services/pricingService";
import productApiServices from "services/productApiServices";
import { Table } from "shared";
import { applyMoneyMask, cleanMoneyMask } from "utils/inputMasks";

import FilterDrawer from "./components/FilterDrawer";
import V4SuggestionCell from "./components/V4SuggestionCell";
import V4SuggestionDialog from "./components/V4SuggestionDialog";
import {
  PRICING_CATALOG_IDS,
  PRICING_V4_CATALOG_ID,
  PRICING_V4_CATALOG_IDS
} from "./mocks";
import {
  buildApplyPriceItem,
  buildPromoBulkItems,
  clearAppliedPromoEdits,
  getPromoProductBaseId
} from "./promoBulkPayload";
import {
  buildPricingStickyCellStyle,
  buildPricingStickyHeadStyle,
  getPricingRowBackground,
  getPricingTableHeadBg,
  PRICING_ALIGNED_CONTENT_STYLE,
  PRICING_PAPER_STYLE,
  PRICING_STICKY_COLUMNS,
  PRICING_TABLE_CONTAINER_STYLE,
  PRICING_TABLE_STYLE,
  useStyles
} from "./styles";
import useV4Suggestions, { productBaseId } from "./useV4Suggestions";
import { v4CurrentPriceDelta } from "./v4CurrentPriceDelta";

const recalcRow = row => {
  const markupBase =
    row.priceForMarkup !== undefined ? row.priceForMarkup : row.preco_final;
  const delta = row.preco_atual
    ? (row.preco_final - row.preco_atual) / row.preco_atual
    : 0;
  const markup = row.cmc ? (markupBase - row.cmc) / row.cmc : null;
  const precoCeasa = Number(row.preco_ceasa);
  const hasValidCeasaPrice =
    Number.isFinite(precoCeasa) && precoCeasa > 0 && row.preco_ceasa !== "";
  const hasValidMarkupBase =
    markupBase !== null &&
    markupBase !== undefined &&
    markupBase !== "" &&
    Number.isFinite(Number(markupBase));
  const markupCeasa =
    hasValidCeasaPrice && hasValidMarkupBase
      ? (Number(markupBase) - precoCeasa) / precoCeasa
      : null;
  return {
    ...row,
    variacao_preco_pct: delta,
    markup_sugerido: markup,
    markup_ceasa: markupCeasa
  };
};

const resolveEffectiveFinalPrice = (
  row,
  displayData,
  editedPrices,
  editedAmeixaPrices,
  equivalentPreviewByRow
) => {
  const displayRow = displayData.find(d => d.id === row.id) ?? row;
  const id = displayRow.id;
  const apiFinalPrice = displayRow.apiFinalPrice ?? displayRow.preco_final;
  const hasEditedAmeixaPrice = editedAmeixaPrices[id] !== undefined;
  const preview = equivalentPreviewByRow[id];

  if (
    hasEditedAmeixaPrice &&
    preview?.sourceMode === "target_ameixa_price" &&
    preview?.loading
  ) {
    return { price: null, pending: true };
  }

  const estimatedFinalPrice =
    hasEditedAmeixaPrice && preview?.sourceMode === "target_ameixa_price"
      ? preview.estimatedFinalPrice
      : null;

  const price =
    estimatedFinalPrice ?? editedPrices[id] ?? apiFinalPrice ?? null;

  return { price, pending: false };
};

const buildMarkupSimulatePayload = (
  pricingRunId,
  pendingSelected,
  displayData,
  editedPrices,
  editedAmeixaPrices,
  equivalentPreviewByRow
) => {
  const selected = [];

  for (const row of pendingSelected) {
    const { price, pending } = resolveEffectiveFinalPrice(
      row,
      displayData,
      editedPrices,
      editedAmeixaPrices,
      equivalentPreviewByRow
    );
    if (pending) {
      return { pending: true, body: null };
    }
    if (price == null || price <= 0) continue;
    selected.push({
      product_base_id: row.product_base_id,
      preco_final: Number(Number(price).toFixed(4))
    });
  }

  return {
    pending: false,
    body: {
      ...(pricingRunId != null
        ? { run_id: pricingRunId }
        : { latest_run: true }),
      selected
    }
  };
};

const formatMarkupPercent = value => {
  if (value == null) return "--";
  return `${(value * 100).toFixed(1)}%`;
};

const getMarkupIndicatorColor = value => {
  if (value == null) return "#bdbdbd";
  return value >= 0.15 ? "#388e3c" : "#e53935";
};

const MarkupCard = ({ value, label, displayValue, tooltip }) => {
  const shown = displayValue ?? formatMarkupPercent(value);
  const color = getMarkupIndicatorColor(value);

  const card = (
    <Box
      display="flex"
      alignItems="center"
      style={{
        gap: "0.5rem",
        border: "1px solid #e0e0e0",
        borderRadius: 999,
        padding: "0.3rem 0.875rem",
        background: "#fafafa"
      }}
    >
      <Box
        style={{
          width: 10,
          height: 10,
          borderRadius: "50%",
          flexShrink: 0,
          background: color
        }}
      />
      <Typography
        style={{
          fontFamily: "Roboto",
          fontWeight: 600,
          fontSize: "0.9rem",
          color: value == null ? "#9e9e9e" : color
        }}
      >
        {shown}
      </Typography>
      <Typography
        style={{
          fontFamily: "Inter",
          fontSize: "0.75rem",
          fontWeight: 500,
          color: "#9e9e9e"
        }}
      >
        {label}
      </Typography>
    </Box>
  );

  if (tooltip) {
    return (
      <Tooltip title={tooltip} arrow placement="bottom">
        <span style={{ display: "inline-flex" }}>{card}</span>
      </Tooltip>
    );
  }

  return card;
};

const parseCurrencyValue = value => {
  if (value == null || value === "") return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;

  const cleaned = String(value)
    .trim()
    .replace(/[^\d,.-]/g, "");
  const normalized =
    cleaned.includes(",") && cleaned.lastIndexOf(",") > cleaned.lastIndexOf(".")
      ? cleaned.replace(/\./g, "").replace(",", ".")
      : cleaned.replace(/,/g, "");
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

const formatCurrency = value => {
  const parsed = parseCurrencyValue(value);

  return parsed != null
    ? parsed.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })
    : "--";
};

const formatPercent = value => {
  if (value == null) return "--";
  const pct = parseFloat((value * 100).toFixed(1));
  return `${pct.toFixed(1)}%`;
};

const formatDate = value => {
  if (!value) return "--";
  const [year, month, day] = String(value).split("T")[0].split("-");
  return `${day}/${month}/${year}`;
};

const formatQty = value =>
  value != null
    ? value.toLocaleString("pt-BR", { maximumFractionDigits: 1 })
    : "—";

const formatBoolLabel = value => {
  if (value == null) return "—";
  return value ? "Sim" : "Não";
};

const SEVERITY_LABELS = {
  sem_promo: "Sem promoção",
  monitorar: "Monitorar",
  promo_leve: "Promo leve",
  promo_moderada: "Promo moderada",
  promo_forte: "Promo forte",
  liquidacao_critica: "Liquidação crítica"
};

const formatSeverity = value => {
  if (value == null || value === "") return "--";
  return SEVERITY_LABELS[value] ?? value;
};

const firstDefined = (...values) =>
  values.find(value => {
    if (value === null || value === undefined) return false;
    if (typeof value === "string" && value.trim() === "") return false;
    return true;
  });

const buildPromoTooltipContent = (promo, promoBatchMeta) => {
  const hasSuggestion = promo?.promo_has_suggestion === true;
  const sections = [];

  if (!hasSuggestion) {
    sections.push({
      heading: null,
      lines: [
        { label: "Promo", value: "Sem sugestão promocional para este produto." }
      ]
    });
  } else {
    // -- Bloco 1: Status ---------------------------------------------------
    sections.push({
      heading: "Status",
      lines: [
        { label: "Severidade", value: formatSeverity(promo.promo_severity) },
        {
          label: "Elegível",
          value: formatBoolLabel(promo.promo_eligible_flag)
        },
        { label: "Em promoção ativa", value: formatBoolLabel(promo.in_promo) }
      ]
    });

    // -- Bloco 2: Preços ---------------------------------------------------
    // Fallback: desconto_vs_referencia (nova lógica) ou desconto_pct_sugerido (antigo)
    const descontoBase = firstDefined(
      promo.desconto_vs_referencia,
      promo.desconto_pct_sugerido
    );
    sections.push({
      heading: "Preços",
      lines: [
        {
          label: "P. Promo recomendado",
          value: formatCurrency(promo.preco_promocional_sugerido)
        },
        {
          label: "Preço zero-waste teórico",
          value: formatCurrency(promo.preco_zero_waste_teorico)
        },
        {
          label: "Preço referência v3",
          value: formatCurrency(promo.preco_referencia_v3)
        },
        {
          label: "Desconto vs referência",
          value: formatPercent(descontoBase)
        },
        {
          label: "Margem promo",
          value: formatPercent(promo.margem_promo)
        },
        {
          label: "Piso comercial aplicado",
          value: formatCurrency(promo.piso_comercial_aplicado)
        }
      ]
    });

    // -- Bloco 3: Estoque e risco ------------------------------------------
    sections.push({
      heading: "Estoque e risco",
      lines: [
        { label: "Excesso", value: formatQty(promo.excesso_qty) },
        { label: "Excesso %", value: formatPercent(promo.excesso_pct) },
        {
          label: "Previsão até validade",
          value: formatQty(promo.forecast_ate_shelf_b)
        },
        { label: "Demanda validade", value: formatQty(promo.demanda_validade) },
        {
          label: "Sobra projetada",
          value: formatQty(promo.sobra_projetada_ate_shelf)
        },
        {
          label: "Zero waste",
          value: formatBoolLabel(promo.atinge_zero_waste_flag)
        }
      ]
    });

    // -- Bloco 4: Decisão --------------------------------------------------
    // Fallback: motivo_promo_revisado (nova lógica) ou promo_reason (antigo)
    const motivoFinal = firstDefined(
      promo.motivo_promo_revisado,
      promo.promo_reason
    );
    sections.push({
      heading: "Decisão",
      lines: [
        {
          label: "Bateu piso comercial",
          value: formatBoolLabel(promo.bateu_piso_comercial)
        },
        {
          label: "Sobra aceita",
          value: formatBoolLabel(promo.sobra_aceita_flag)
        },
        { label: "Motivo", value: motivoFinal || "--" }
      ]
    });
  }

  // -- Bloco extra: aviso de batch desalinhado ------------------------------
  if (promoBatchMeta?.promo_stale) {
    const runLabel =
      promoBatchMeta.promo_run != null
        ? ` (run ${promoBatchMeta.promo_run})`
        : "";
    const staleLines = [
      {
        label: "Batch promocional",
        value: `Dados de outro batch${runLabel}.`
      }
    ];
    if (promoBatchMeta.promo_stale_reason) {
      staleLines.push({
        label: "Detalhe",
        value: promoBatchMeta.promo_stale_reason
      });
    }
    sections.push({ heading: "Aviso", lines: staleLines });
  }

  // sections sempre tem ao menos um item (caso "Sem sugestão" ou blocos de detalhe).
  return (
    <Typography
      variant="caption"
      component="div"
      style={{
        whiteSpace: "pre-wrap",
        lineHeight: 1.6,
        display: "block",
        maxWidth: 320
      }}
    >
      {sections.map((section, sIdx) => (
        <span
          key={`section-${sIdx}`}
          style={{
            display: "block",
            marginTop: sIdx === 0 ? 0 : 6
          }}
        >
          {section.heading && (
            <span
              style={{
                display: "block",
                fontWeight: 700,
                textTransform: "uppercase",
                fontSize: "0.7rem",
                opacity: 0.85,
                marginBottom: 2
              }}
            >
              {section.heading}
            </span>
          )}
          {section.lines.map((line, lIdx) => (
            <span
              key={`${section.heading || "x"}-${line.label}-${lIdx}`}
              style={{ display: "block" }}
            >
              <strong>{line.label}:</strong> {line.value}
            </span>
          ))}
        </span>
      ))}
    </Typography>
  );
};

const renderPromoCell = (row, promoBatchMeta, classes) => {
  const promo = row.promo ?? {};
  const eligible = promo.promo_eligible_flag === true;
  const label = eligible ? (
    <span className={`${classes.flagBadge} ${classes.badgeSuccess}`}>Sim</span>
  ) : (
    <span className={`${classes.flagBadge} ${classes.badgeNeutral}`}>--</span>
  );
  const tooltipContent = buildPromoTooltipContent(promo, promoBatchMeta);

  return (
    <Tooltip title={tooltipContent} arrow placement="top" enterDelay={150}>
      <span style={{ display: "inline-flex", cursor: "help" }}>{label}</span>
    </Tooltip>
  );
};

const PARECER_CONFIG = {
  aprovado: { Icon: CheckIcon, color: "#2e7d32", label: "Aprovado" },
  mock_aprovado: {
    Icon: CheckIcon,
    color: "#2e7d32",
    label: "Aprovado via mock"
  },
  atencao: { Icon: WarningIcon, color: "#f57f17", label: "Atenção" },
  fallback_congelado_curva_a: {
    Icon: WarningIcon,
    color: "#f57f17",
    label: "Congelado por regra de Curva A"
  },
  fallback_erro: {
    Icon: WarningIcon,
    color: "#f57f17",
    label: "Fallback por erro"
  },
  rejeitar: { Icon: CloseIcon, color: "#c62828", label: "Rejeitado" },
  rejeitar_com_acao: {
    Icon: CloseIcon,
    color: "#c62828",
    label: "Rejeitado com ação necessária"
  }
};

const COMPACT_HEAD = { padding: "6px 8px" };
const compactCell = () => ({ padding: "6px 8px" });
const DEFAULT_EXCLUDED_CATEGORIES = ["Cerealista", "PNC", "Mercearia"];

const Pricing = () => {
  const classes = useStyles();
  const { addPopup } = usePopup();
  const addPopupRef = useRef(addPopup);
  addPopupRef.current = addPopup;
  const theme = useTheme();

  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [categoriesLoaded, setCategoriesLoaded] = useState(false);
  const [filtersReady, setFiltersReady] = useState(false);
  const [activeFilters, setActiveFilters] = useState({});
  const [editedPrices, setEditedPrices] = useState({});
  const [priceOrigins, setPriceOrigins] = useState({});
  const [editedAmeixaPrices, setEditedAmeixaPrices] = useState({});
  const [equivalentPreviewByRow, setEquivalentPreviewByRow] = useState({});
  const [confirmedRows, setConfirmedRows] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectionResetCounter, setSelectionResetCounter] = useState(0);
  const [recalcKey, setRecalcKey] = useState(0);
  const [isApplying, setIsApplying] = useState(false);
  const [editedPromo, setEditedPromo] = useState({});
  const [isUpdatingPromo, setIsUpdatingPromo] = useState(false);
  const [isSyncingPromo, setIsSyncingPromo] = useState(false);
  const [syncConfirmOpen, setSyncConfirmOpen] = useState(false);
  const simulationCacheRef = useRef({});
  const simulationRequestSeqRef = useRef({});
  const markupSimulateRequestSeqRef = useRef(0);

  const [markupGeneralProjected, setMarkupGeneralProjected] = useState(null);
  const [markupGeneralSuggestedIa, setMarkupGeneralSuggestedIa] =
    useState(null);
  const [pricingRunId, setPricingRunId] = useState(null);
  const [markupCardsMeta, setMarkupCardsMeta] = useState(null);
  const [markupSelection, setMarkupSelection] = useState(null);
  const [markupSelectionStatus, setMarkupSelectionStatus] = useState("empty");
  const [markupSelectionError, setMarkupSelectionError] = useState(null);
  const [hasHorizontalScrollOffset, setHasHorizontalScrollOffset] =
    useState(false);
  const tableContainerRef = useRef(null);
  const [promoBatchMeta, setPromoBatchMeta] = useState({
    promo_stale: false,
    promo_stale_reason: null,
    promo_run: null,
    promo_aligned_with_pricing_run: false
  });
  const hasInitializedDefaultFiltersRef = useRef(false);
  const {
    data,
    params,
    onChange,
    changeNow,
    status,
    refresh,
    handleSearch,
    count
  } = useTableParams({
    service: (p, options) => pricingService.getSuggestions(p, options),
    paramsDefault: {},
    enabled: filtersReady,
    onSuccess: rest => {
      setMarkupGeneralProjected(rest.markup_geral_projetado ?? null);
      setMarkupGeneralSuggestedIa(rest.markup_geral_sugerido_ia ?? null);
      setPricingRunId(rest.markup_cards_meta?.run_id ?? null);
      setMarkupCardsMeta(rest.markup_cards_meta ?? null);
      setPromoBatchMeta({
        promo_stale: rest.promo_stale === true,
        promo_stale_reason: rest.promo_stale_reason ?? null,
        promo_run: rest.promo_run ?? null,
        promo_aligned_with_pricing_run:
          rest.promo_aligned_with_pricing_run === true
      });
    }
  });

  const v4CatalogId = PRICING_V4_CATALOG_ID;
  const v4 = useV4Suggestions(
    data,
    v4CatalogId,
    JSON.stringify(params),
    status,
    PRICING_V4_CATALOG_IDS,
    pricingRunId
  );

  const displayData = useMemo(() => {
    return data.map(row => {
      const apiFinalPrice = row.preco_final;
      const hasEditedPrice = editedPrices[row.id] !== undefined;
      const hasEditedAmeixaPrice = editedAmeixaPrices[row.id] !== undefined;
      const preview = equivalentPreviewByRow[row.id];
      const estimatedFinalPrice =
        hasEditedAmeixaPrice && preview?.sourceMode === "target_ameixa_price"
          ? preview.estimatedFinalPrice
          : null;
      const estimatedAmeixaPrice =
        hasEditedPrice && preview?.sourceMode === "price"
          ? preview.estimatedAmeixaPrice
          : null;
      const mergedFinalPrice =
        estimatedFinalPrice ?? editedPrices[row.id] ?? apiFinalPrice;
      const priceForMarkup = hasEditedAmeixaPrice
        ? estimatedFinalPrice ?? apiFinalPrice
        : mergedFinalPrice;
      const novo_p_atual =
        editedAmeixaPrices[row.id] ??
        estimatedAmeixaPrice ??
        row.novo_p_atual_equivalente ??
        null;
      return recalcRow({
        ...row,
        preco_final: mergedFinalPrice,
        priceForMarkup,
        apiFinalPrice,
        novo_p_atual,
        equivalentPreview: preview,
        isFinalPriceEstimated: estimatedFinalPrice != null,
        isAmeixaPriceEstimated: estimatedAmeixaPrice != null,
        priceOrigin: priceOrigins[row.id]
      });
    });
  }, [
    data,
    editedPrices,
    editedAmeixaPrices,
    equivalentPreviewByRow,
    priceOrigins
  ]);

  useEffect(() => {
    const pendingSelected = selectedRows.filter(
      row => !confirmedRows.includes(row.id)
    );

    if (pendingSelected.length === 0) {
      markupSimulateRequestSeqRef.current += 1;
      setMarkupSelection(null);
      setMarkupSelectionStatus("empty");
      setMarkupSelectionError(null);
      return;
    }

    const payloadResult = buildMarkupSimulatePayload(
      pricingRunId,
      pendingSelected,
      displayData,
      editedPrices,
      editedAmeixaPrices,
      equivalentPreviewByRow
    );

    if (payloadResult.pending) {
      setMarkupSelectionStatus("loading");
      setMarkupSelectionError(null);
      return;
    }

    setMarkupSelectionStatus("loading");
    setMarkupSelectionError(null);

    const timeoutId = window.setTimeout(async () => {
      const requestSeq = markupSimulateRequestSeqRef.current + 1;
      markupSimulateRequestSeqRef.current = requestSeq;

      try {
        const { data } = await pricingService.markupSimulate(
          payloadResult.body
        );

        if (markupSimulateRequestSeqRef.current !== requestSeq) return;

        setMarkupSelection(data?.markup_geral_selecao ?? null);
        setMarkupSelectionStatus("ready");
        setMarkupSelectionError(null);
      } catch (err) {
        if (markupSimulateRequestSeqRef.current !== requestSeq) return;

        const msg =
          err?.response?.data?.detail ||
          err?.response?.data?.msg ||
          err?.message ||
          "Nao foi possivel simular o markup selecionado.";
        setMarkupSelection(null);
        setMarkupSelectionStatus("error");
        setMarkupSelectionError(
          typeof msg === "string" ? msg : JSON.stringify(msg)
        );
      }
    }, 400);

    return () => {
      markupSimulateRequestSeqRef.current += 1;
      window.clearTimeout(timeoutId);
    };
  }, [
    selectedRows,
    confirmedRows,
    editedPrices,
    editedAmeixaPrices,
    equivalentPreviewByRow,
    pricingRunId,
    displayData
  ]);

  useEffect(() => {
    let cancelled = false;
    const loadCategories = async () => {
      try {
        const { data } = await productApiServices.getCategories();
        if (cancelled) return;
        const list = Array.isArray(data) ? data : data?.results ?? [];
        setCategoryOptions(
          list
            .filter(c => c?.name)
            .map(c => ({ id: c.name, label: c.name }))
            .sort((a, b) => a.label.localeCompare(b.label, "pt-BR"))
        );
      } catch (error) {
        if (cancelled || error?.response?.status === 401) return;
        addPopupRef.current({
          type: "error",
          title: "Erro ao carregar categorias",
          description: error?.response?.data?.msg
        });
      } finally {
        if (!cancelled) setCategoriesLoaded(true);
      }
    };
    loadCategories();
    return () => {
      cancelled = true;
    };
  }, []);

  const getDefaultSelectedCategories = useCallback(
    categories =>
      categories
        .map(category => category.id)
        .filter(
          categoryName => !DEFAULT_EXCLUDED_CATEGORIES.includes(categoryName)
        ),
    []
  );

  useEffect(() => {
    if (hasInitializedDefaultFiltersRef.current || !categoriesLoaded) {
      return;
    }

    const defaultCategories = getDefaultSelectedCategories(categoryOptions);
    hasInitializedDefaultFiltersRef.current = true;

    setActiveFilters({
      categorias: defaultCategories
    });

    (changeNow || onChange)({
      page: 1,
      categoria: undefined,
      categorias:
        defaultCategories.length > 0 ? defaultCategories.join(",") : undefined,
      em_promocao: undefined,
      curva: undefined,
      variacao_ameixa_direction: undefined
    });
    setFiltersReady(true);
  }, [
    categoriesLoaded,
    categoryOptions,
    getDefaultSelectedCategories,
    changeNow,
    onChange
  ]);

  const clearEquivalentPreview = useCallback(id => {
    simulationRequestSeqRef.current[id] =
      (simulationRequestSeqRef.current[id] || 0) + 1;
    setEquivalentPreviewByRow(prev => {
      if (prev[id] === undefined) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);

  const simulateEquivalentPreview = useCallback(
    async (id, productBaseId, mode, value) => {
      if (!productBaseId || value == null || value <= 0) {
        clearEquivalentPreview(id);
        return;
      }

      const normalizedValue = Number(value).toFixed(4);
      const cacheKey = `${productBaseId}:${mode}:${normalizedValue}`;
      const cached = simulationCacheRef.current[cacheKey];
      if (cached) {
        setEquivalentPreviewByRow(prev => ({ ...prev, [id]: cached }));
        return;
      }

      const requestSeq = (simulationRequestSeqRef.current[id] || 0) + 1;
      simulationRequestSeqRef.current[id] = requestSeq;
      setEquivalentPreviewByRow(prev => ({
        ...prev,
        [id]: { sourceMode: mode, loading: true, error: null }
      }));

      try {
        const item =
          mode === "price"
            ? { productBaseId, price: value }
            : { productBaseId, target_ameixa_price: value };
        const { data } = await pricingService.simulateSkuPrices({
          catalog_ids: PRICING_CATALOG_IDS,
          items: [item]
        });

        if (simulationRequestSeqRef.current[id] !== requestSeq) return;

        const calc = data?.calculations?.[0];
        if (!calc || calc.error) {
          setEquivalentPreviewByRow(prev => ({
            ...prev,
            [id]: {
              sourceMode: mode,
              loading: false,
              error: calc?.error || "Nao foi possivel simular."
            }
          }));
          return;
        }

        const preview = {
          sourceMode: mode,
          loading: false,
          error: null,
          estimatedFinalPrice: calc.price_equivalente_calculado ?? null,
          estimatedAmeixaPrice: calc.preco_ameixa_estimado ?? null,
          targetAmeixaPrice: calc.target_ameixa_price ?? null,
          porcaoAlvo: calc.porcao_alvo ?? null,
          origemPorcaoAlvo: calc.origem_porcao_alvo ?? null,
          diferencaTargetEstimado: calc.diferenca_target_estimado ?? null
        };
        simulationCacheRef.current[cacheKey] = preview;
        setEquivalentPreviewByRow(prev => ({ ...prev, [id]: preview }));
      } catch (err) {
        if (simulationRequestSeqRef.current[id] !== requestSeq) return;
        const msg =
          err?.response?.data?.detail ||
          err?.response?.data?.msg ||
          err?.message ||
          "Nao foi possivel simular.";
        setEquivalentPreviewByRow(prev => ({
          ...prev,
          [id]: {
            sourceMode: mode,
            loading: false,
            error: typeof msg === "string" ? msg : JSON.stringify(msg)
          }
        }));
      }
    },
    [clearEquivalentPreview]
  );

  const handlePriceChange = useCallback(
    (
      id,
      productBaseId,
      rawValue,
      apiFinalPrice,
      equivalentValue,
      priceOrigin
    ) => {
      const parsed = cleanMoneyMask(rawValue);
      if (parsed == null) return;
      const comparisonTolerance = priceOrigin === "V4" ? 0.00000001 : 0.001;
      if (
        editedPrices[id] === undefined ||
        Math.abs(parsed - editedPrices[id]) >= comparisonTolerance
      ) {
        setPriceOrigins(prev => {
          const next = { ...prev };
          delete next[id];
          return next;
        });
      }
      if (
        equivalentValue != null &&
        Math.abs(parsed - equivalentValue) < 0.001 &&
        editedPrices[id] === undefined
      ) {
        return;
      }
      // Compare to API baseline, not merged row.preco_final — otherwise blur
      // without edits cleared editedPrices and reverted the input.
      if (Math.abs(parsed - apiFinalPrice) < comparisonTolerance) {
        setEditedPrices(prev => {
          const next = { ...prev };
          delete next[id];
          return next;
        });
        clearEquivalentPreview(id);
        return;
      }
      setEditedPrices(prev => ({ ...prev, [id]: parsed }));
      // P. Final editado → descarta Novo P. Atual pendente para esta linha
      setEditedAmeixaPrices(prev => {
        if (prev[id] === undefined) return prev;
        const next = { ...prev };
        delete next[id];
        return next;
      });
      simulateEquivalentPreview(id, productBaseId, "price", parsed);
    },
    [clearEquivalentPreview, editedPrices, simulateEquivalentPreview]
  );

  const handleResetPrice = useCallback(
    id => {
      setPriceOrigins(prev => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      setEditedPrices(prev => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      clearEquivalentPreview(id);
      setRecalcKey(k => k + 1);
    },
    [clearEquivalentPreview]
  );

  const handleAmeixaPriceChange = useCallback(
    (id, productBaseId, rawValue, equivalentValue) => {
      setPriceOrigins(prev => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      const trimmed = String(rawValue).trim();
      if (trimmed === "" || trimmed === "0" || trimmed === "0,00") {
        setEditedAmeixaPrices(prev => {
          if (prev[id] === undefined) return prev;
          const next = { ...prev };
          delete next[id];
          return next;
        });
        clearEquivalentPreview(id);
        return;
      }
      const parsed = cleanMoneyMask(rawValue);
      if (parsed == null || parsed <= 0) return;
      if (
        equivalentValue != null &&
        Math.abs(parsed - equivalentValue) < 0.001 &&
        editedAmeixaPrices[id] === undefined
      ) {
        return;
      }
      setEditedAmeixaPrices(prev => ({ ...prev, [id]: parsed }));
      // Novo P. Atual editado → descarta P. Final pendente para esta linha
      setEditedPrices(prev => {
        if (prev[id] === undefined) return prev;
        const next = { ...prev };
        delete next[id];
        return next;
      });
      simulateEquivalentPreview(
        id,
        productBaseId,
        "target_ameixa_price",
        parsed
      );
    },
    [clearEquivalentPreview, editedAmeixaPrices, simulateEquivalentPreview]
  );

  const handleResetAmeixaPrice = useCallback(
    id => {
      setEditedAmeixaPrices(prev => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      clearEquivalentPreview(id);
      setRecalcKey(k => k + 1);
    },
    [clearEquivalentPreview]
  );

  const handleSelectV4Reference = useCallback(
    (productId, price) => {
      const row = displayData.find(item => productBaseId(item) === productId);
      if (!row || confirmedRows.includes(row.id)) return;
      handlePriceChange(
        row.id,
        productId,
        price.toLocaleString("pt-BR", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 4
        }),
        row.apiFinalPrice,
        null,
        "V4"
      );
      setPriceOrigins(prev => ({ ...prev, [row.id]: "V4" }));
      setRecalcKey(value => value + 1);
    },
    [displayData, confirmedRows, handlePriceChange]
  );

  const handleApplyAll = useCallback(async () => {
    const pending = selectedRows.filter(r => !confirmedRows.includes(r.id));

    if (pending.length === 0) {
      addPopup({
        type: "info",
        title: "Nenhum produto selecionado",
        description: "Selecione ao menos um produto para aplicar os preços."
      });
      return;
    }

    const resolveRow = r => displayData.find(d => d.id === r.id) ?? r;
    const pendingResolved = pending.map(resolveRow);

    setIsApplying(true);
    try {
      const { data } = await pricingService.calcSkuPrices({
        catalog_ids: PRICING_CATALOG_IDS,
        items: pendingResolved.map(r => {
          return buildApplyPriceItem(r, editedAmeixaPrices, editedPromo);
        })
      });

      const calcs = data.calculations || [];
      const catalogUp = data.catalog_updates || [];

      const calcErrors = pendingResolved
        .map((r, i) => ({ row: r, err: calcs[i]?.error }))
        .filter(x => x.err);

      const catalogOk =
        PRICING_CATALOG_IDS.length === 0 ||
        (catalogUp.length > 0 && catalogUp.every(u => u.error == null));

      let idsToConfirm = [];
      if (catalogOk || PRICING_CATALOG_IDS.length === 0) {
        idsToConfirm = pendingResolved
          .map((r, i) => (!calcs[i]?.error ? r.id : null))
          .filter(Boolean);
      }

      if (PRICING_CATALOG_IDS.length > 0 && !catalogOk) {
        const catMsg = catalogUp
          .map(u => u.error)
          .filter(Boolean)
          .join(" | ");
        addPopup({
          type: "error",
          title: "Falha ao atualizar catálogo",
          description:
            catMsg ||
            "Não foi possível confirmar a atualização na Ameixa. Nenhum item foi marcado como aplicado."
        });
      } else if (idsToConfirm.length === 0) {
        addPopup({
          type: "error",
          title: "Nenhum preço aplicado",
          description:
            calcErrors.map(x => `${x.row.nome_produto}: ${x.err}`).join("; ") ||
            "Verifique os dados e tente novamente."
        });
      } else {
        addPopup({
          type: "success",
          title: "Preços aplicados",
          description: "As alterações foram salvas com sucesso."
        });
        await refresh();
        setEditedPrices(prev => {
          const next = { ...prev };
          idsToConfirm.forEach(id => {
            delete next[id];
          });
          return next;
        });
        setEditedAmeixaPrices(prev => {
          const next = { ...prev };
          idsToConfirm.forEach(id => {
            delete next[id];
          });
          return next;
        });
        setEditedPromo(prev => {
          return clearAppliedPromoEdits(prev, pendingResolved, idsToConfirm);
        });
        setRecalcKey(k => k + 1);
        setSelectionResetCounter(n => n + 1);
      }
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.msg ||
        err?.message ||
        String(err);
      addPopup({
        type: "error",
        title: "Erro ao aplicar preços",
        description: typeof msg === "string" ? msg : JSON.stringify(msg)
      });
    } finally {
      setIsApplying(false);
    }
  }, [
    addPopup,
    confirmedRows,
    displayData,
    editedAmeixaPrices,
    editedPromo,
    selectedRows,
    refresh
  ]);

  const handleApplyFilters = filters => {
    setActiveFilters(filters);
    onChange({
      page: 1,
      categoria: undefined,
      categorias:
        filters.categorias && filters.categorias.length > 0
          ? filters.categorias.join(",")
          : undefined,
      em_promocao:
        filters.em_promocao !== undefined ? filters.em_promocao : undefined,
      curva: filters.curva !== undefined ? filters.curva : undefined,
      variacao_ameixa_direction:
        filters.variacao_ameixa_direction !== undefined
          ? filters.variacao_ameixa_direction
          : undefined
    });
  };

  const handleClearFilters = () => {
    const defaultCategories = getDefaultSelectedCategories(categoryOptions);
    setActiveFilters({
      categorias: defaultCategories
    });
    onChange({
      page: 1,
      categoria: undefined,
      categorias:
        defaultCategories.length > 0 ? defaultCategories.join(",") : undefined,
      em_promocao: undefined,
      curva: undefined,
      variacao_ameixa_direction: undefined
    });
  };

  const pendingPromoCount = Object.keys(editedPromo).length;
  const promoRunId = promoBatchMeta.promo_run;
  const canSyncPromo =
    promoRunId != null && !isSyncingPromo && status !== "pending";

  const handleTogglePromo = useCallback((productBaseId, value) => {
    if (productBaseId == null) return;
    setEditedPromo(prev => ({ ...prev, [productBaseId]: Boolean(value) }));
  }, []);

  const handleToggleAllPromo = useCallback(
    value => {
      const updates = {};
      displayData.forEach(row => {
        const productBaseId = getPromoProductBaseId(row);
        if (productBaseId != null) {
          updates[productBaseId] = Boolean(value);
        }
      });
      setEditedPromo(prev => ({ ...prev, ...updates }));
    },
    [displayData]
  );

  const handleCancelPromo = useCallback(() => {
    setEditedPromo({});
  }, []);

  const handleBulkUpdatePromo = useCallback(async () => {
    if (pendingPromoCount === 0) return;
    const { items, invalidKeys } = buildPromoBulkItems(editedPromo);

    if (invalidKeys.length > 0 || items.length === 0) {
      console.warn("Pricing promo update skipped: invalid product_base_id", {
        invalidKeys,
        editedPromo
      });
      addPopup({
        type: "error",
        title: "Erro ao atualizar promoções",
        description:
          "Há alterações de promoção com product_base_id inválido. A tabela não foi enviada."
      });
      return;
    }

    setIsUpdatingPromo(true);
    try {
      await pricingService.bulkPublishPromocoes(items);
      addPopup({
        type: "success",
        title: "Promoções atualizadas",
        description:
          "Badge de promoção publicado/removido no site. Nenhum preço foi alterado."
      });
      setEditedPromo({});
      await refresh();
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.msg ||
        err?.message ||
        String(err);
      addPopup({
        type: "error",
        title: "Erro ao atualizar promoções",
        description: typeof msg === "string" ? msg : JSON.stringify(msg)
      });
    } finally {
      setIsUpdatingPromo(false);
    }
  }, [addPopup, editedPromo, pendingPromoCount, refresh]);

  const handleConfirmSyncPromo = useCallback(async () => {
    if (promoRunId == null) return;
    setSyncConfirmOpen(false);
    setIsSyncingPromo(true);
    try {
      await pricingService.syncPromotions(promoRunId);
      addPopup({
        type: "success",
        title: "Sincronização concluída",
        description: `Run promocional ${promoRunId} sincronizado. Nenhum preço foi alterado na Ameixa.`
      });
      setEditedPromo({});
      await refresh();
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.msg ||
        err?.message ||
        String(err);
      addPopup({
        type: "error",
        title: "Erro ao sincronizar promoções",
        description: typeof msg === "string" ? msg : JSON.stringify(msg)
      });
    } finally {
      setIsSyncingPromo(false);
    }
  }, [addPopup, promoRunId, refresh]);

  const allVisiblePromoActive = useMemo(
    () =>
      displayData.length > 0 &&
      displayData.every(row => {
        const productBaseId = getPromoProductBaseId(row);
        return productBaseId != null
          ? editedPromo[productBaseId] ?? row.promo?.in_promo ?? false
          : row.promo?.in_promo ?? false;
      }),
    [displayData, editedPromo]
  );

  const getDeltaClass = delta => {
    if (delta > 0.0001) return classes.deltaPositive;
    if (delta < -0.0001) return classes.deltaNegative;
    return classes.deltaNeutral;
  };

  const getDeltaIcon = delta => {
    if (delta > 0.0001) return <ArrowUpwardIcon style={{ fontSize: 14 }} />;
    if (delta < -0.0001) return <ArrowDownwardIcon style={{ fontSize: 14 }} />;
    return <RemoveIcon style={{ fontSize: 14 }} />;
  };

  const activeFiltersCount = [
    activeFilters.categorias?.length > 0,
    activeFilters.em_promocao !== undefined &&
      activeFilters.em_promocao !== null,
    activeFilters.curva !== undefined && activeFilters.curva !== null,
    activeFilters.variacao_ameixa_direction === "up" ||
      activeFilters.variacao_ameixa_direction === "down"
  ].filter(Boolean).length;

  const pendingCount = selectedRows.filter(
    r => !confirmedRows.includes(r.id)
  ).length;

  const rowBackgroundState = useMemo(
    () => ({
      confirmedRows,
      editedPrices,
      editedAmeixaPrices,
      editedPromo
    }),
    [confirmedRows, editedPrices, editedAmeixaPrices, editedPromo]
  );

  const getRowBackground = useCallback(
    (row, rowIndex) =>
      getPricingRowBackground(row, rowIndex, rowBackgroundState),
    [rowBackgroundState]
  );

  const pricingTableHeadBg = useMemo(
    () => getPricingTableHeadBg(theme),
    [theme]
  );

  const syncHorizontalScrollOffset = useCallback(() => {
    const container = tableContainerRef.current;
    if (!container) return;
    setHasHorizontalScrollOffset(container.scrollLeft > 0);
  }, []);

  const handleTableContainerScroll = useCallback(event => {
    setHasHorizontalScrollOffset(event.currentTarget.scrollLeft > 0);
  }, []);

  useEffect(() => {
    syncHorizontalScrollOffset();
  }, [displayData, status, syncHorizontalScrollOffset]);

  const stickyCheckboxHeadStyle = useMemo(
    () => ({
      paddingLeft: "1.5rem",
      ...buildPricingStickyHeadStyle(
        PRICING_STICKY_COLUMNS.checkbox.left,
        PRICING_STICKY_COLUMNS.checkbox.width,
        pricingTableHeadBg
      )
    }),
    [pricingTableHeadBg]
  );

  const stickyCheckboxCellStyle = useCallback(
    (row, rowIndex) => ({
      ...buildPricingStickyCellStyle(
        PRICING_STICKY_COLUMNS.checkbox.left,
        PRICING_STICKY_COLUMNS.checkbox.width,
        getRowBackground(row, rowIndex)
      )
    }),
    [getRowBackground]
  );

  const columns = useMemo(() => {
    const tableHead = {
      ...COMPACT_HEAD,
      backgroundColor: pricingTableHeadBg
    };

    const stickyHead = field => {
      const { left, width, isLast } = PRICING_STICKY_COLUMNS[field];
      const showLastSeparator = isLast && hasHorizontalScrollOffset;
      return {
        ...tableHead,
        ...buildPricingStickyHeadStyle(
          left,
          width,
          pricingTableHeadBg,
          showLastSeparator
        )
      };
    };

    const stickyCell = (field, row, rowIndex) => {
      const { left, width, isLast } = PRICING_STICKY_COLUMNS[field];
      const showLastSeparator = isLast && hasHorizontalScrollOffset;
      return {
        ...compactCell(),
        ...buildPricingStickyCellStyle(
          left,
          width,
          getPricingRowBackground(row, rowIndex, rowBackgroundState),
          showLastSeparator
        )
      };
    };

    const getParecerIcon = value => {
      const config = value ? PARECER_CONFIG[value] : null;
      if (!config) return "--";
      const { Icon, color, label } = config;
      return (
        <Tooltip title={label} arrow placement="top" enterDelay={150}>
          <Icon style={{ fontSize: 16, color }} aria-label={label} />
        </Tooltip>
      );
    };

    const getCurvaBadge = curva => {
      const raw = typeof curva === "string" ? curva.trim() : curva;
      const valueLower = typeof raw === "string" ? raw.toLowerCase() : raw;
      const isEmpty =
        valueLower == null ||
        valueLower === "" ||
        valueLower === "--" ||
        valueLower === "sem curva";
      if (isEmpty) {
        return (
          <span className={`${classes.flagBadge} ${classes.badgeNeutral}`}>
            --
          </span>
        );
      }
      const curvaClass =
        raw === "A"
          ? classes.badgeSuccess
          : raw === "B"
          ? classes.badgeWarning
          : classes.badgeFail;
      return (
        <span className={`${classes.flagBadge} ${curvaClass}`}>{raw}</span>
      );
    };

    return [
      {
        field: "product_base_id",
        label: "ID",
        align: "center",
        style: stickyHead("product_base_id"),
        cellStyle: (row, rowIndex) =>
          stickyCell("product_base_id", row, rowIndex),
        renderCell: ({ data: row }) => (
          <Typography
            variant="caption"
            style={{ color: "#888", fontWeight: 600 }}
          >
            {row.product_base_id}
          </Typography>
        )
      },
      {
        field: "nome_produto",
        label: "Produto",
        style: stickyHead("nome_produto"),
        cellStyle: (row, rowIndex) => stickyCell("nome_produto", row, rowIndex),
        renderCell: ({ data: row }) => (
          <Box display="flex" alignItems="center" style={{ gap: 4 }}>
            <Typography
              variant="body2"
              className={classes.stickyProductName}
              style={{ lineHeight: 1.4, flex: 1, minWidth: 0 }}
            >
              {row.nome_produto}
            </Typography>
            {row.justificativa_gemini ? (
              <Tooltip
                title={
                  <Typography
                    variant="caption"
                    style={{
                      whiteSpace: "pre-wrap",
                      lineHeight: 1.6,
                      display: "block",
                      maxWidth: 280
                    }}
                  >
                    {row.justificativa_gemini}
                  </Typography>
                }
                arrow
                placement="right"
                enterDelay={150}
              >
                <HelpOutlineIcon
                  style={{
                    fontSize: 16,
                    color: "#bdbdbd",
                    cursor: "help",
                    flexShrink: 0,
                    marginTop: 2,
                    transition: "color 0.15s"
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.color = "#757575";
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.color = "#bdbdbd";
                  }}
                />
              </Tooltip>
            ) : (
              <span style={{ width: 17, flexShrink: 0 }} />
            )}
          </Box>
        )
      },
      {
        field: "curva",
        label: "Curva",
        align: "center",
        disabledSort: true,
        style: stickyHead("curva"),
        cellStyle: (row, rowIndex) => stickyCell("curva", row, rowIndex),
        renderCell: ({ data: row }) => getCurvaBadge(row.curva)
      },
      {
        field: "parecer_gemini",
        label: "Parecer Gemini",
        align: "center",
        disabledSort: true,
        style: stickyHead("parecer_gemini"),
        cellStyle: (row, rowIndex) => ({
          ...stickyCell("parecer_gemini", row, rowIndex),
          textAlign: "center"
        }),
        renderCell: ({ data: row }) => getParecerIcon(row.parecer_gemini)
      },
      {
        field: "cmc",
        label: "CMC",
        align: "right",
        disabledSort: true,
        style: { width: "80px", ...tableHead },
        cellStyle: compactCell,
        formatter: value => formatCurrency(value)
      },
      {
        field: "preco_atual",
        label: "P. Médio",
        align: "right",
        style: { width: "80px", ...tableHead },
        cellStyle: compactCell,
        formatter: value => formatCurrency(value)
      },
      {
        field: "preco_ameixa",
        label: "P. Atual",
        align: "right",
        disabledSort: true,
        style: { width: "80px", ...tableHead },
        cellStyle: compactCell,
        formatter: value => formatCurrency(value)
      },
      {
        field: "preco_ceasa",
        label: "Ceasa",
        align: "right",
        disabledSort: true,
        style: { width: "78px", ...tableHead },
        cellStyle: compactCell,
        formatter: value => formatCurrency(value)
      },
      {
        field: "preco_mercado_mediano",
        label: "Mediana Mercado",
        align: "right",
        disabledSort: true,
        style: { width: "100px", ...tableHead },
        cellStyle: compactCell,
        formatter: value => formatCurrency(value)
      },
      {
        field: "preco_sugerido_ia",
        label: "Sug. V3",
        align: "right",
        disabledSort: true,
        style: { width: "78px", ...tableHead },
        cellStyle: compactCell,
        formatter: value => formatCurrency(value)
      },
      {
        field: "suggestion_v4",
        label: "Sug. V4",
        align: "right",
        disabledSort: true,
        style: { minWidth: "112px", ...tableHead },
        cellStyle: compactCell,
        renderCell: ({ data: row }) => <V4SuggestionCell row={row} state={v4} />
      },
      {
        field: "preco_final",
        label: "P. Final",
        align: "center",
        style: { minWidth: "120px", ...tableHead },
        cellStyle: compactCell,
        renderCell: ({ data: row }) => {
          const isConfirmed = confirmedRows.includes(row.id);
          const isEdited = editedPrices[row.id] !== undefined;
          const preview = row.equivalentPreview;
          const isEstimated = row.isFinalPriceEstimated;
          const statusText =
            preview?.loading && preview?.sourceMode === "target_ameixa_price"
              ? "calculando..."
              : isEstimated
              ? "estimado"
              : preview?.error && preview?.sourceMode === "target_ameixa_price"
              ? "--"
              : null;
          return (
            <Box>
              {row.priceOrigin === "V4" && isEdited ? (
                <Typography variant="caption" color="primary">
                  Referência V4
                </Typography>
              ) : null}
              <Box display="flex" alignItems="center" style={{ gap: 4 }}>
                <Tooltip
                  title="Resetar preço"
                  arrow
                  disableHoverListener={!isEdited || isConfirmed}
                >
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => handleResetPrice(row.id)}
                      disabled={!isEdited || isConfirmed}
                      style={{
                        color: "#bbb",
                        padding: 2,
                        flexShrink: 0,
                        visibility:
                          isEdited && !isConfirmed ? "visible" : "hidden"
                      }}
                    >
                      <UndoIcon style={{ fontSize: 14 }} />
                    </IconButton>
                  </span>
                </Tooltip>
                <TextField
                  key={`price-${row.id}-${recalcKey}-${row.preco_final}`}
                  className={classes.priceInput}
                  variant="outlined"
                  size="small"
                  defaultValue={
                    row.priceOrigin === "V4"
                      ? Number(row.preco_final).toLocaleString("pt-BR", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 4
                        })
                      : applyMoneyMask(row.preco_final)
                  }
                  disabled={isConfirmed}
                  onBlur={e =>
                    handlePriceChange(
                      row.id,
                      row.product_base_id,
                      e.target.value,
                      row.apiFinalPrice,
                      isEstimated ? row.preco_final : null,
                      row.priceOrigin
                    )
                  }
                  onKeyDown={e => {
                    if (e.key === "Enter") {
                      e.target.blur();
                      return;
                    }
                    const controlKeys = [
                      "Backspace",
                      "Delete",
                      "Tab",
                      "ArrowLeft",
                      "ArrowRight",
                      "Home",
                      "End"
                    ];
                    const isDigit = /^\d$/.test(e.key);
                    const isSeparator = e.key === "," || e.key === ".";
                    const isControl =
                      controlKeys.includes(e.key) || e.ctrlKey || e.metaKey;
                    if (!isDigit && !isSeparator && !isControl)
                      e.preventDefault();
                  }}
                  inputProps={{ style: { textAlign: "right" } }}
                  InputProps={{
                    startAdornment: (
                      <Typography
                        variant="caption"
                        style={{
                          color: "#888",
                          marginRight: 2,
                          userSelect: "none"
                        }}
                      >
                        R$
                      </Typography>
                    )
                  }}
                />
              </Box>
              {statusText && (
                <Tooltip title={preview?.error || "Valor estimado"} arrow>
                  <Typography
                    variant="caption"
                    style={{
                      color: preview?.error ? "#c62828" : "#757575",
                      fontSize: "0.68rem"
                    }}
                  >
                    {statusText}
                  </Typography>
                </Tooltip>
              )}
              {row.preco_final < row.cmc && (
                <Typography className={classes.warningText}>
                  ⚠️ Abaixo do custo
                </Typography>
              )}
            </Box>
          );
        }
      },
      {
        field: "variacao_preco_pct",
        label: "Δ%",
        align: "center",
        style: { width: "68px", ...tableHead },
        cellStyle: compactCell,
        renderCell: ({ data: row }) => (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            className={getDeltaClass(row.variacao_preco_pct)}
            style={{ gap: 2 }}
          >
            {getDeltaIcon(row.variacao_preco_pct)}
            {formatPercent(row.variacao_preco_pct)}
          </Box>
        )
      },
      {
        field: "novo_p_atual",
        label: "Novo P. Atual",
        align: "center",
        disabledSort: true,
        style: { minWidth: "140px", ...tableHead },
        cellStyle: compactCell,
        renderCell: ({ data: row }) => {
          const isEdited = editedAmeixaPrices[row.id] !== undefined;
          const preview = row.equivalentPreview;
          const isEstimated = row.isAmeixaPriceEstimated;
          const statusText =
            preview?.loading && preview?.sourceMode === "price"
              ? "calculando..."
              : isEstimated
              ? "estimado"
              : preview?.error && preview?.sourceMode === "price"
              ? "--"
              : null;
          return (
            <Box display="flex" alignItems="center" style={{ gap: 4 }}>
              <Tooltip
                title="Resetar Novo P. Atual"
                arrow
                disableHoverListener={!isEdited}
              >
                <span>
                  <IconButton
                    size="small"
                    onClick={() => handleResetAmeixaPrice(row.id)}
                    disabled={!isEdited}
                    style={{
                      color: "#bbb",
                      padding: 2,
                      flexShrink: 0,
                      visibility: isEdited ? "visible" : "hidden"
                    }}
                  >
                    <UndoIcon style={{ fontSize: 14 }} />
                  </IconButton>
                </span>
              </Tooltip>
              <Box>
                <TextField
                  key={`ameixa-${row.id}-${recalcKey}-${
                    row.novo_p_atual ?? ""
                  }`}
                  className={classes.priceInput}
                  variant="outlined"
                  size="small"
                  defaultValue={
                    row.novo_p_atual != null
                      ? applyMoneyMask(row.novo_p_atual)
                      : ""
                  }
                  placeholder="--"
                  onBlur={e =>
                    handleAmeixaPriceChange(
                      row.id,
                      row.product_base_id,
                      e.target.value,
                      isEstimated
                        ? row.novo_p_atual
                        : row.novo_p_atual_equivalente ?? null
                    )
                  }
                  onKeyDown={e => {
                    if (e.key === "Enter") {
                      e.target.blur();
                      return;
                    }
                    const controlKeys = [
                      "Backspace",
                      "Delete",
                      "Tab",
                      "ArrowLeft",
                      "ArrowRight",
                      "Home",
                      "End"
                    ];
                    const isDigit = /^\d$/.test(e.key);
                    const isSeparator = e.key === "," || e.key === ".";
                    const isControl =
                      controlKeys.includes(e.key) || e.ctrlKey || e.metaKey;
                    if (!isDigit && !isSeparator && !isControl)
                      e.preventDefault();
                  }}
                  inputProps={{ style: { textAlign: "right" } }}
                  InputProps={{
                    startAdornment: (
                      <Typography
                        variant="caption"
                        style={{
                          color: "#888",
                          marginRight: 2,
                          userSelect: "none"
                        }}
                      >
                        R$
                      </Typography>
                    )
                  }}
                />
                {statusText && (
                  <Tooltip title={preview?.error || "Valor estimado"} arrow>
                    <Typography
                      variant="caption"
                      style={{
                        color: preview?.error ? "#c62828" : "#757575",
                        fontSize: "0.68rem"
                      }}
                    >
                      {statusText}
                    </Typography>
                  </Tooltip>
                )}
              </Box>
            </Box>
          );
        }
      },
      {
        field: "variacao_ameixa_pct",
        label: "Δ% P. Atual",
        align: "center",
        style: { width: "90px", ...tableHead },
        cellStyle: compactCell,
        renderCell: ({ data: row }) => {
          const base = row.preco_ameixa;
          const novo = row.novo_p_atual;
          if (base == null || base === 0 || novo == null) {
            return (
              <Typography variant="caption" style={{ color: "#bbb" }}>
                --
              </Typography>
            );
          }
          const delta = (novo - base) / base;
          return (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              className={getDeltaClass(delta)}
              style={{ gap: 2 }}
            >
              {getDeltaIcon(delta)}
              {formatPercent(delta)}
            </Box>
          );
        }
      },
      {
        field: "variacao_ameixa_v4_pct",
        label: "Δ% P. Atual V4",
        align: "center",
        disabledSort: true,
        style: { minWidth: "112px", ...tableHead },
        cellStyle: compactCell,
        renderCell: ({ data: row }) => {
          const result = v4CurrentPriceDelta(
            row,
            v4.results[productBaseId(row)],
            v4.status
          );
          if (result.delta === null)
            return (
              <Tooltip title={result.reason} arrow>
                <Typography variant="caption" color="textSecondary">
                  --
                </Typography>
              </Tooltip>
            );
          const { delta, reference, comparison, current, suggested } = result;
          const displayedDelta = Number((delta * 100).toFixed(1)) / 100;
          const tooltip = [
            `V4: ${formatCurrency(
              suggested
            )} por unidade base; P. Atual: ${formatCurrency(
              current
            )}. Mesma embalagem de ${comparison.portion_base} porções base.`,
            `Análise de ${reference.date}.`,
            reference.expired ? "Referência vencida." : null,
            reference.secondary
          ]
            .filter(Boolean)
            .join(" ");
          return (
            <Tooltip title={tooltip} arrow>
              <Box>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  className={getDeltaClass(displayedDelta)}
                  style={{ gap: 2 }}
                >
                  {displayedDelta !== 0 ? getDeltaIcon(displayedDelta) : null}
                  {formatPercent(delta)}
                </Box>
              </Box>
            </Tooltip>
          );
        }
      },
      {
        field: "markup_sugerido",
        label: "Markup",
        align: "right",
        style: { width: "68px", ...tableHead },
        cellStyle: compactCell,
        renderCell: ({ data: row }) => (
          <span
            className={
              row.markup_sugerido >= 0.15
                ? classes.markupBom
                : classes.markupRuim
            }
          >
            {formatPercent(row.markup_sugerido)}
          </span>
        )
      },
      {
        field: "markup_ceasa",
        label: "Markup Ceasa",
        align: "right",
        style: { width: "92px", ...tableHead },
        cellStyle: compactCell,
        renderCell: ({ data: row }) => {
          const rawMarkupCeasa = row.markup_ceasa;
          const hasMarkupCeasa =
            rawMarkupCeasa !== null &&
            rawMarkupCeasa !== undefined &&
            rawMarkupCeasa !== "" &&
            Number.isFinite(Number(rawMarkupCeasa));
          return (
            <span
              className={
                hasMarkupCeasa
                  ? rawMarkupCeasa >= 0.15
                    ? classes.markupBom
                    : classes.markupRuim
                  : undefined
              }
            >
              {formatPercent(rawMarkupCeasa)}
            </span>
          );
        }
      },
      {
        field: "projecao_vendas",
        label: "Proj.",
        align: "right",
        disabledSort: true,
        style: { width: "60px", ...tableHead },
        cellStyle: compactCell,
        formatter: value => (value != null ? value.toFixed(1) : "--")
      },
      {
        field: "vendas_dia",
        label: "Vnd/Dia",
        align: "right",
        disabledSort: true,
        style: { width: "68px", ...tableHead },
        cellStyle: compactCell,
        formatter: value => (value != null ? value.toFixed(1) : "--")
      },
      {
        field: "previsao_d0",
        label: "Prev. D0",
        align: "right",
        disabledSort: true,
        style: { width: "68px", ...tableHead },
        cellStyle: compactCell,
        formatter: value => (value != null ? value.toFixed(1) : "--")
      },
      {
        field: "estoque_do_dia",
        label: "Est. Dia",
        align: "right",
        disabledSort: true,
        style: { width: "68px", ...tableHead },
        cellStyle: compactCell,
        formatter: value => (value != null ? value.toFixed(1) : "--")
      },
      {
        field: "estoque_inicial",
        label: "Est. Ini.",
        align: "right",
        disabledSort: true,
        style: { width: "72px", ...tableHead },
        cellStyle: compactCell,
        formatter: value => (value != null ? value.toFixed(1) : "--")
      },
      {
        field: "promo_eligible_flag",
        label: (
          <Box
            display="inline-flex"
            alignItems="center"
            justifyContent="center"
            style={{ gap: 4 }}
          >
            <span>Promo</span>
            {promoBatchMeta.promo_stale ? (
              <Tooltip
                title={
                  promoBatchMeta.promo_stale_reason
                    ? `Dados promocionais de outro batch (run ${
                        promoBatchMeta.promo_run ?? "?"
                      }). ${promoBatchMeta.promo_stale_reason}`
                    : `Dados promocionais de outro batch (run ${
                        promoBatchMeta.promo_run ?? "?"
                      }).`
                }
                arrow
                placement="top"
                enterDelay={150}
              >
                <WarningIcon
                  style={{ fontSize: 14, color: "#f57f17", cursor: "help" }}
                  aria-label="Batch promocional desalinhado"
                />
              </Tooltip>
            ) : null}
          </Box>
        ),
        align: "center",
        disabledSort: true,
        style: { width: "64px", ...tableHead },
        cellStyle: () => ({ ...compactCell(), textAlign: "center" }),
        renderCell: ({ data: row }) =>
          renderPromoCell(row, promoBatchMeta, classes)
      },
      {
        field: "preco_promocional_sugerido",
        label: "P. Promo",
        align: "right",
        disabledSort: true,
        style: { width: "80px", ...tableHead },
        cellStyle: compactCell,
        renderCell: ({ data: row }) => (
          <Typography variant="caption">
            {formatCurrency(row.promo?.preco_promocional_sugerido)}
          </Typography>
        )
      },
      {
        field: "validade_estoque",
        label: "Validade",
        align: "center",
        disabledSort: true,
        style: { width: "76px", ...tableHead },
        cellStyle: compactCell,
        renderCell: ({ data: row }) => (
          <Typography variant="caption">
            {formatDate(row.promo?.validade_estoque)}
          </Typography>
        )
      },
      {
        field: "dias_cobertura",
        label: "Dias de estoque cobertos",
        align: "right",
        disabledSort: true,
        style: { width: "132px", ...tableHead },
        cellStyle: compactCell,
        renderCell: ({ data: row }) => {
          const hasValue =
            row.dias_cobertura !== null &&
            row.dias_cobertura !== undefined &&
            row.dias_cobertura !== "";
          const value = hasValue ? Number(row.dias_cobertura) : NaN;
          return (
            <Typography variant="caption">
              {Number.isFinite(value)
                ? value.toLocaleString("pt-BR", { maximumFractionDigits: 1 })
                : "--"}
            </Typography>
          );
        }
      },
      {
        field: "promo_in_promo_toggle",
        label: "Ativar",
        align: "center",
        disabledSort: true,
        style: { width: "70px", ...tableHead },
        cellStyle: compactCell,
        renderCell: ({ data: row }) => {
          const productBaseId = getPromoProductBaseId(row);
          const current =
            productBaseId != null
              ? editedPromo[productBaseId] ?? row.promo?.in_promo ?? false
              : row.promo?.in_promo ?? false;
          return (
            <Switch
              size="small"
              checked={current}
              color="primary"
              disabled={productBaseId == null}
              onChange={e => handleTogglePromo(productBaseId, e.target.checked)}
            />
          );
        }
      }
    ];
  }, [
    classes,
    confirmedRows,
    editedPrices,
    editedAmeixaPrices,
    recalcKey,
    promoBatchMeta,
    editedPromo,
    handleTogglePromo,
    handlePriceChange,
    handleResetPrice,
    handleAmeixaPriceChange,
    handleResetAmeixaPrice,
    rowBackgroundState,
    pricingTableHeadBg,
    hasHorizontalScrollOffset,
    v4.results,
    v4.status,
    v4.open,
    v4.retry
  ]);

  const markupSelectionValue =
    markupSelectionStatus === "ready" ? markupSelection : null;
  const markupSelectionDisplay =
    markupSelectionStatus === "loading"
      ? "..."
      : markupSelectionStatus === "ready"
      ? formatMarkupPercent(markupSelection)
      : "--";
  const markupSelectionTooltip =
    markupSelectionStatus === "empty"
      ? "Selecione produtos para simular o markup parcial"
      : markupSelectionStatus === "error"
      ? markupSelectionError
      : undefined;

  return (
    <>
      <ContainerPage style={{ maxWidth: "100%" }}>
        <Box style={PRICING_ALIGNED_CONTENT_STYLE}>
          <Box
            display="flex"
            alignItems="center"
            flexWrap="wrap"
            style={{ gap: "1rem", marginBottom: "0.5rem", width: "100%" }}
          >
            <TitlePage>Precificação</TitlePage>

            <Box
              display="flex"
              alignItems="center"
              flexWrap="wrap"
              style={{ gap: "0.75rem" }}
            >
              <MarkupCard
                value={status === "pending" ? null : markupGeneralProjected}
                label="Markup Geral Projetado"
              />
              <MarkupCard
                value={status === "pending" ? null : markupGeneralSuggestedIa}
                label="Markup Geral Sugerido V3"
              />
              <MarkupCard
                value={markupSelectionValue}
                label="Markup Selecionado"
                displayValue={markupSelectionDisplay}
                tooltip={markupSelectionTooltip}
              />
            </Box>
          </Box>

          <Table
            columns={columns}
            data={displayData}
            onChange={onChange}
            params={params}
            status={status}
            refresh={refresh}
            count={count}
            paginationServer
            handleSearch={handleSearch}
            placeholder="Produto..."
            size="small"
            isMobile={false}
            checkboxSelection
            onSelect={setSelectedRows}
            selectionResetCounter={selectionResetCounter}
            tableStyle={PRICING_TABLE_STYLE}
            paperStyle={PRICING_PAPER_STYLE}
            tableContainerStyle={PRICING_TABLE_CONTAINER_STYLE}
            tableContainerRef={tableContainerRef}
            onTableContainerScroll={handleTableContainerScroll}
            stickyCheckboxHeadStyle={stickyCheckboxHeadStyle}
            stickyCheckboxStyle={stickyCheckboxCellStyle}
            rowStyle={(row, rowIndex) => ({
              backgroundColor: getRowBackground(row, rowIndex)
            })}
            filters={
              <>
                <Tooltip title="Filtrar dados" arrow>
                  <IconButton
                    onClick={() => setIsFilterDrawerOpen(true)}
                    centerRipple={false}
                    style={{
                      height: "56px",
                      width: "56px",
                      border: `2px solid ${
                        activeFiltersCount > 0
                          ? theme.palette.primary.main
                          : "#e0e0e0"
                      }`,
                      borderRadius: "4px",
                      backgroundColor:
                        activeFiltersCount > 0 ? "#e3f2fd" : "#fff",
                      transition: "all 0.2s ease-in-out"
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.borderColor =
                        theme.palette.primary.dark;
                      e.currentTarget.style.backgroundColor = "#f5f5f5";
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.borderColor =
                        activeFiltersCount > 0
                          ? theme.palette.primary.main
                          : "#e0e0e0";
                      e.currentTarget.style.backgroundColor =
                        activeFiltersCount > 0 ? "#e3f2fd" : "#fff";
                    }}
                  >
                    <FilterListIcon
                      style={{
                        fontSize: "24px",
                        color:
                          activeFiltersCount > 0
                            ? theme.palette.primary.main
                            : "#666"
                      }}
                    />
                  </IconButton>
                </Tooltip>

                <Box
                  display="flex"
                  alignItems="center"
                  flexWrap="wrap"
                  style={{ gap: "0.75rem", marginLeft: "auto" }}
                >
                  {activeFiltersCount > 0 && (
                    <Typography
                      variant="body2"
                      style={{
                        color: theme.palette.primary.main,
                        fontWeight: 600,
                        whiteSpace: "nowrap"
                      }}
                    >
                      • {activeFiltersCount} filtro(s) ativo(s)
                    </Typography>
                  )}

                  <Box
                    display="flex"
                    alignItems="center"
                    flexWrap="wrap"
                    style={{
                      gap: "0.5rem",
                      borderLeft: "1px solid #e0e0e0",
                      paddingLeft: "0.75rem"
                    }}
                  >
                    <Typography
                      variant="caption"
                      style={{
                        color: "#757575",
                        fontWeight: 600,
                        whiteSpace: "nowrap"
                      }}
                    >
                      Preços
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={<CheckIcon />}
                      onClick={handleApplyAll}
                      disabled={pendingCount === 0 || isApplying}
                    >
                      Aplicar Preços Alterados
                      {pendingCount > 0 ? ` (${pendingCount})` : ""}
                    </Button>
                  </Box>

                  <Box
                    display="flex"
                    alignItems="center"
                    flexWrap="wrap"
                    style={{
                      gap: "0.5rem",
                      borderLeft: "1px solid #e0e0e0",
                      paddingLeft: "0.75rem"
                    }}
                  >
                    <Typography
                      variant="caption"
                      style={{
                        color: "#757575",
                        fontWeight: 600,
                        whiteSpace: "nowrap"
                      }}
                    >
                      Promoção
                    </Typography>
                    {promoBatchMeta.promo_stale ? (
                      <Tooltip
                        title={
                          promoBatchMeta.promo_stale_reason ||
                          "Dados promocionais de outro batch."
                        }
                        arrow
                      >
                        <Typography
                          variant="caption"
                          style={{
                            color: "#f57f17",
                            fontWeight: 600,
                            whiteSpace: "nowrap"
                          }}
                        >
                          Batch desalinhado
                        </Typography>
                      </Tooltip>
                    ) : null}
                    <Tooltip
                      title="Atualiza apenas produto_em_promocao. Não altera preços na Ameixa."
                      arrow
                    >
                      <span>
                        <Button
                          variant="outlined"
                          onClick={() => setSyncConfirmOpen(true)}
                          disabled={!canSyncPromo}
                        >
                          {isSyncingPromo
                            ? "Sincronizando..."
                            : "Sincronizar Promoções"}
                        </Button>
                      </span>
                    </Tooltip>
                    <Tooltip title="Ativar/desativar todos visíveis" arrow>
                      <Box
                        display="flex"
                        alignItems="center"
                        style={{ gap: 4 }}
                      >
                        <Switch
                          size="small"
                          checked={allVisiblePromoActive}
                          color="primary"
                          onChange={e => handleToggleAllPromo(e.target.checked)}
                          disabled={displayData.length === 0}
                        />
                        <Typography
                          variant="body2"
                          style={{ whiteSpace: "nowrap", color: "#555" }}
                        >
                          Ativar todos
                        </Typography>
                      </Box>
                    </Tooltip>
                    {pendingPromoCount > 0 ? (
                      <Button
                        variant="outlined"
                        onClick={handleCancelPromo}
                        disabled={isUpdatingPromo}
                      >
                        Cancelar
                      </Button>
                    ) : null}
                    <Tooltip
                      title="Salva status de promoção. Não aplica P. Promo nem preços na Ameixa."
                      arrow
                    >
                      <span>
                        <Button
                          variant="outlined"
                          startIcon={<CheckIcon />}
                          onClick={handleBulkUpdatePromo}
                          disabled={pendingPromoCount === 0 || isUpdatingPromo}
                        >
                          Atualizar promoções
                          {pendingPromoCount > 0
                            ? ` (${pendingPromoCount})`
                            : ""}
                        </Button>
                      </span>
                    </Tooltip>
                  </Box>
                </Box>
              </>
            }
          />
        </Box>
      </ContainerPage>
      <V4SuggestionDialog
        state={v4}
        catalogId={v4CatalogId}
        catalogIds={PRICING_V4_CATALOG_IDS}
        applicationCatalogIds={PRICING_CATALOG_IDS}
        onSelectReference={handleSelectV4Reference}
        selectionDisabled={
          isApplying ||
          displayData.some(
            row =>
              productBaseId(row) === v4.selected?.id &&
              confirmedRows.includes(row.id)
          )
        }
      />

      <Dialog
        open={syncConfirmOpen}
        onClose={() => !isSyncingPromo && setSyncConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Sincronizar promoções</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            <Typography variant="body2" paragraph>
              Esta ação desativa todos os produtos marcados como em promoção
              ativa e reativa apenas os elegíveis anti-waste do run promocional{" "}
              <strong>{promoRunId ?? "—"}</strong>.
            </Typography>
            <Typography variant="body2" paragraph>
              Não altera preços na Ameixa e não aplica o P. Promo sugerido.
            </Typography>
            {promoBatchMeta.promo_stale ? (
              <Typography
                variant="body2"
                paragraph
                style={{ color: "#f57f17" }}
              >
                Atenção: o batch promocional exibido está desalinhado do run de
                precificação atual.
                {promoBatchMeta.promo_stale_reason
                  ? ` ${promoBatchMeta.promo_stale_reason}`
                  : null}
              </Typography>
            ) : null}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setSyncConfirmOpen(false)}
            disabled={isSyncingPromo}
          >
            Cancelar
          </Button>
          <Button
            color="primary"
            variant="contained"
            onClick={handleConfirmSyncPromo}
            disabled={isSyncingPromo || promoRunId == null}
          >
            Confirmar sincronização
          </Button>
        </DialogActions>
      </Dialog>

      <FilterDrawer
        open={isFilterDrawerOpen}
        onClose={() => setIsFilterDrawerOpen(false)}
        onApply={handleApplyFilters}
        onClear={handleClearFilters}
        initialParams={activeFilters}
        categories={categoryOptions}
      />
    </>
  );
};

export default Pricing;
